import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return val; }
};

const ArrowLeft = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
);

const AlertIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const CompletionConfirmPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get(`projects/${projectId}`),
            api.get(`invoices/by_project/${projectId}`),
        ]).then(([pr, inv]) => {
            setProject(pr.data);
            setInvoice(inv.data);
        }).catch(console.error)
          .finally(() => setLoading(false));
    }, [projectId]);

    const handleConfirm = async () => {
        if (!window.confirm('仕上がりを確認して完了手続きを実行しますか？\nこの操作は取り消せません。')) return;
        setConfirming(true);
        setError(null);
        try {
            await api.post(`projects/${projectId}/confirm_completion`);
            navigate(`/customer/review/${projectId}`);
        } catch {
            setError('処理に失敗しました。しばらくしてから再度お試しください。');
        } finally {
            setConfirming(false);
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const contractor = project?.contractor;
    const condition = project?.condition;
    const schedule = project?.schedule;

    return (
        <div style={{ minHeight: 'calc(100vh - 64px)', background: '#f5f6f8' }}>

            {/* ページヘッダー */}
            <div style={{ background: '#005c53', padding: '24px 0' }}>
                <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 20px' }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', marginBottom: 12, padding: 0 }}
                    >
                        <ArrowLeft /> 戻る
                    </button>
                    <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                        仕上がり確認・完了手続き
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.7)', margin: '4px 0 0', fontSize: '0.875rem' }}>
                        工事が完了しました。内容をご確認ください。
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 20px' }}>

                {/* 完了通知カード */}
                <div style={{ background: '#fff', borderRadius: 10, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16, border: '1px solid #e0e4ea' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <span style={{ padding: '3px 12px', background: '#fef9c3', color: '#a16207', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600 }}>
                            完了確認待ち
                        </span>
                    </div>

                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px' }}>工事完了通知</h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 8 }}>
                            <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>業者名</span>
                            <span style={{ fontSize: '0.85rem', color: '#1a1a2e', fontWeight: 500 }}>{contractor?.company_name || '—'}</span>
                        </div>
                        {condition?.work_types?.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 8 }}>
                                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>工事種別</span>
                                <span style={{ fontSize: '0.85rem', color: '#1a1a2e' }}>{condition.work_types.join('・')}</span>
                            </div>
                        )}
                        {(condition?.location_prefecture || condition?.location_city) && (
                            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 8 }}>
                                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>施工場所</span>
                                <span style={{ fontSize: '0.85rem', color: '#1a1a2e' }}>
                                    {condition.location_prefecture}{condition.location_city}
                                </span>
                            </div>
                        )}
                        {schedule?.start_date && (
                            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 8 }}>
                                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>工事日</span>
                                <span style={{ fontSize: '0.85rem', color: '#1a1a2e' }}>{fmtDate(schedule.start_date)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* 請求金額カード */}
                {invoice && (
                    <div style={{ background: '#fff', borderRadius: 10, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16, border: '1px solid #e0e4ea' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px' }}>お支払い金額</h3>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>請求金額（税込）</span>
                            <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1a1a2e' }}>¥{fmt(invoice.amount)}</span>
                        </div>
                        {invoice.tax_amount > 0 && (
                            <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '4px 0 0', textAlign: 'right' }}>
                                うち消費税 ¥{fmt(invoice.tax_amount)}
                            </p>
                        )}
                    </div>
                )}

                {/* 注意事項 */}
                <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 10, padding: '16px 20px', marginBottom: 24, display: 'flex', gap: 12 }}>
                    <div style={{ flexShrink: 0, marginTop: 2 }}><AlertIcon /></div>
                    <div>
                        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e', margin: '0 0 4px' }}>完了手続きについて</p>
                        <p style={{ fontSize: '0.83rem', color: '#78350f', margin: 0, lineHeight: 1.6 }}>
                            「完了手続きをする」をクリックすると、施工業者への代金のお支払いが実行されます。
                            実際に仕上がりをご確認の上、実行してください。この操作は取り消せません。
                        </p>
                    </div>
                </div>

                {error && (
                    <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: '0.875rem', color: '#b91c1c' }}>
                        {error}
                    </div>
                )}

                {/* ボタン */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <button
                        onClick={handleConfirm}
                        disabled={confirming}
                        style={{
                            width: '100%', padding: '15px',
                            background: confirming ? '#9ca3af' : '#005c53',
                            color: '#fff', border: 'none', borderRadius: 8,
                            fontWeight: 700, fontSize: '1rem',
                            cursor: confirming ? 'default' : 'pointer',
                            transition: 'background 0.15s',
                        }}
                    >
                        {confirming ? '処理中...' : '仕上がりを確認して完了手続きをする'}
                    </button>
                    <button
                        onClick={() => navigate(-1)}
                        disabled={confirming}
                        style={{ width: '100%', padding: '13px', background: '#fff', color: '#6b7280', border: '1px solid #e0e4ea', borderRadius: 8, fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer' }}
                    >
                        まだ確認できていない
                    </button>
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default CompletionConfirmPage;
